import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { ComboboxChangeEventDetails, ComboboxChangeReason } from '@opendoor/atoms/combobox';
import { debounce } from 'es-toolkit';

import type { AutoCompletePrediction } from '../components/api/addressTypes';
import { buildPlacesUrl, generateGoogleMapsSessionToken } from './googleMaps';

const DEBOUNCE_INVOKE_WAIT_MS = 100;

/**
 * Base UI drives the combobox input text on its own in two cases this field must
 * ignore: it blanks a typed query once the popup closes without a selection, and
 * it rewrites the text to the chosen label on commit (which `selectPrediction`
 * already does, so honoring it would refetch suggestions for the full address).
 * Here the query is app state, so only edits the user made to the field count.
 * Add a reason when adopting a part that produces one, e.g. `Combobox.Clear`.
 */
export const USER_EDIT_REASONS: ComboboxChangeReason[] = ['input-change', 'escape-key'];

interface UseRemoteAutocompleteOptions {
  /** Builds the suggestion request for a query, e.g. address vs. city/region. */
  buildUrl: (query: string, sessionToken: string) => string;
  /**
   * Narrows a raw suggestion list to the ones this field accepts. Runs after
   * shared normalization, so `id`, `ariaLabel`, `description`, and `source` are
   * already populated.
   */
  selectPredictions: (predictions: AutoCompletePrediction[]) => AutoCompletePrediction[];
  onError?: (error: Error) => void;
  /** Controlled search text. Pair with `onChange`. */
  value?: string;
  /** Initial search text for uncontrolled use. Ignored when `value` is set. */
  defaultValue?: string;
  onChange?: (searchTerm: string) => void;
}

/**
 * The shared half of a suggest-then-resolve field: debounced fetch of
 * suggestions from the `/_address-input` edge worker, then a place lookup for
 * whichever one the user picks.
 *
 * Callers supply the two policy pieces — which endpoint to query
 * (`buildUrl`) and which suggestions are acceptable (`selectPredictions`) — and
 * decide what a resolved place means via `selectPrediction`'s handler.
 */
export const useRemoteAutocomplete = ({
  buildUrl,
  selectPredictions,
  onError,
  value,
  defaultValue,
  onChange,
}: UseRemoteAutocompleteOptions) => {
  const [predictions, setPredictions] = useState<AutoCompletePrediction[]>([]);
  const [internalSearchTerm, setInternalSearchTerm] = useState(defaultValue ?? '');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionToken] = useState(() => generateGoogleMapsSessionToken());
  const abortControllerRef = useRef<AbortController | null>(null);

  const isControlled = value !== undefined;
  const searchTerm = isControlled ? value : internalSearchTerm;

  const updateSearchTerm = useCallback(
    (newValue: string) => {
      if (!isControlled) {
        setInternalSearchTerm(newValue);
      }
      onChange?.(newValue);
    },
    [isControlled, onChange],
  );

  const clear = useCallback(() => {
    setPredictions([]);
  }, []);

  const fetchPredictions = useMemo(
    () =>
      debounce(async (query: string) => {
        if (!query) {
          clear();
          return;
        }

        // A slower earlier request must not land after a faster later one and
        // repopulate the list with stale suggestions.
        abortControllerRef.current?.abort();
        abortControllerRef.current = new AbortController();

        try {
          const response = await fetch(buildUrl(query, sessionToken), {
            signal: abortControllerRef.current.signal,
          });
          if (!response.ok) {
            throw new Error(`Autocomplete request failed: ${response.status}`);
          }
          const data = await response.json();
          const raw: AutoCompletePrediction[] = Array.isArray(data?.predictions)
            ? data.predictions
            : [];

          const normalized = raw.map((prediction) => {
            const description = prediction.description.replace(', USA', '');
            return {
              ...prediction,
              description,
              id: prediction.place_id || prediction.addressToken || description,
              ariaLabel: description,
              source: data.source,
            };
          });

          setPredictions(selectPredictions(normalized));
        } catch (error) {
          if (error instanceof Error && error.name === 'AbortError') {
            return;
          }
          clear();
          onError?.(error as Error);
        }
      }, DEBOUNCE_INVOKE_WAIT_MS),
    [buildUrl, selectPredictions, sessionToken, onError, clear],
  );

  useEffect(() => {
    return () => {
      fetchPredictions.cancel();
      abortControllerRef.current?.abort();
    };
  }, [fetchPredictions]);

  const search = useCallback(
    (newValue: string, details?: ComboboxChangeEventDetails) => {
      if (details && !USER_EDIT_REASONS.includes(details.reason)) {
        return;
      }
      updateSearchTerm(newValue);
      fetchPredictions(newValue);
      if (newValue === '') {
        clear();
      }
    },
    [updateSearchTerm, fetchPredictions, clear],
  );

  /**
   * Commits a suggestion: adopts its description, resolves it to a place, and
   * hands that to `onResolved`. Stays in the loading state until `onResolved`
   * settles, so follow-on work (address validation) keeps the spinner up.
   */
  const selectPrediction = useCallback(
    async <P>(
      prediction: AutoCompletePrediction,
      onResolved: (place: P) => void | Promise<void>,
    ) => {
      updateSearchTerm(prediction.description);
      setPredictions([]);
      setIsLoading(true);

      const url =
        prediction.source === 'google'
          ? buildPlacesUrl({ source: 'google', placeId: prediction.place_id, sessionToken })
          : buildPlacesUrl({ source: 'casa', addressToken: prediction.addressToken, sessionToken });

      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Place details request failed: ${response.status}`);
        }
        await onResolved(await response.json());
      } catch (error) {
        onError?.(error as Error);
      } finally {
        setIsLoading(false);
      }
    },
    [updateSearchTerm, sessionToken, onError],
  );

  return { searchTerm, predictions, isLoading, search, selectPrediction, clear };
};
